import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  cardStyle,
  chipStyle,
  dangerButtonStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  LOYALTY_CONFIG_DOC_ID,
  challengesRepoFor,
  loyaltyConfigRepoFor,
} from '../lib/repositories/loyalty-repo.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { logger } from '../lib/logger.js';
import {
  isChallengeActive,
  money,
  type Challenge,
  type ChallengeCriterion,
  type ChallengeReward,
  type LoyaltyConfig,
  type VoucherTemplate,
} from '@kilat/shared';

/**
 * /loyalty/challenges — operator-side CRUD for TIME-BOUNDED windowed goals.
 *
 * The windowed sibling of the (seeded, lifetime) achievements: an operator
 * authors a challenge ("5 washes in October → free wash") with a start/end
 * window, a goal (visits or spend), and a reward (points / voucher / none).
 * `onTicketCollected` advances each active challenge's per-customer progress
 * post-transaction and issues the reward on completion.
 *
 * Writes go through the rules-guarded `challengesRepoFor` (owner/manager, same
 * `loyaltyProgram/{doc=**}` rule as tiers/vouchers). The engine only reads a
 * challenge while `enabledFeatures.challenges` is on AND it is active + inside
 * its window — so this page surfaces the master toggle's state and every
 * challenge's live/upcoming/ended status, with explicit
 * loading / empty / error / saving states (no dead-ends).
 */
export function LoyaltyChallengesPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const challengesSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = challengesRepoFor(tenantId);
    return (cb: (vs: Challenge[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: challenges, loading } = useTrackedSnapshot<Challenge[]>(
    ['challenges', tenantId],
    challengesSubscribe,
  );

  // Voucher templates power the reward picker (voucher-kind rewards).
  const templatesSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = voucherTemplatesRepoFor(tenantId);
    return (cb: (v: VoucherTemplate[]) => void) => repo.listSnapshot(cb, () => cb([]));
  }, [tenantId]);
  const { data: templates } = useTrackedSnapshot<VoucherTemplate[]>(
    ['challenges-voucher-templates', tenantId],
    templatesSub,
  );

  // Master toggle state — read-only surfaced so the operator knows whether the
  // engine is actually reading their challenges (it's edited on /loyalty/settings).
  const configSub = useMemo(() => {
    if (!tenantId) return null;
    const repo = loyaltyConfigRepoFor(tenantId);
    return (cb: (v: LoyaltyConfig | null) => void) => repo.snapshot(LOYALTY_CONFIG_DOC_ID, cb);
  }, [tenantId]);
  const { data: config } = useTrackedSnapshot<LoyaltyConfig | null>(
    ['challenges-config', tenantId],
    configSub,
  );

  const [editingId, setEditingId] = useState<string | null>(null);

  if (!tenantId) return <Screen body={t.t('dashboard.challenges.signInPrompt')} />;
  if (loading) return <Screen body={t.t('dashboard.challenges.loading')} />;

  const list = (challenges ?? []).slice().sort((a, b) => {
    const ams = a.createdAt?.toMillis?.() ?? 0;
    const bms = b.createdAt?.toMillis?.() ?? 0;
    return bms - ams;
  });
  const editing = editingId ? (list.find((c) => c.id === editingId) ?? null) : null;
  const enabled = config?.enabledFeatures.challenges === true;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.challenges.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t(
              list.length === 1
                ? 'dashboard.challenges.countOne'
                : 'dashboard.challenges.countOther',
              { count: list.length },
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Link to="/loyalty/settings" style={ghostLinkStyle}>
            {t.t('dashboard.challenges.backToLoyalty')}
          </Link>
          <Link to="/queue" style={ghostLinkStyle}>
            {t.t('dashboard.challenges.backToQueue')}
          </Link>
        </div>
      </header>

      {config && !enabled ? (
        <div style={disabledBannerStyle} role="status">
          <p style={{ margin: 0, fontWeight: 500 }}>{t.t('dashboard.challenges.disabledTitle')}</p>
          <p style={{ ...helpTextStyle, marginTop: 'var(--space-1)' }}>
            {t.t('dashboard.challenges.disabledBody')}{' '}
            <Link to="/loyalty/settings" style={{ color: 'var(--color-primary)' }}>
              {t.t('dashboard.challenges.disabledLink')}
            </Link>
          </p>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-6)',
          alignItems: 'flex-start',
        }}
      >
        <section style={{ ...cardStyle, flex: '2 1 320px', minWidth: 0 }}>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.challenges.listHeading')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {list.map((challenge) => (
              <ChallengeRow
                key={challenge.id}
                tenantId={tenantId}
                challenge={challenge}
                templates={templates ?? []}
                isEditing={challenge.id === editingId}
                onEdit={() => setEditingId(challenge.id)}
              />
            ))}
            {list.length === 0 ? (
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  textAlign: 'center',
                  margin: `var(--space-6) 0`,
                }}
              >
                {t.t('dashboard.challenges.empty')}
              </p>
            ) : null}
          </div>
        </section>

        <section style={{ ...cardStyle, flex: '1 1 320px', minWidth: 0, maxWidth: 460 }}>
          <ChallengeForm
            tenantId={tenantId}
            editing={editing}
            templates={templates ?? []}
            onDone={() => setEditingId(null)}
            onCancelEdit={() => setEditingId(null)}
          />
        </section>
      </div>
    </main>
  );
}

// ── window status ────────────────────────────────────────────────────────

type WindowStatus = 'upcoming' | 'live' | 'ended';

function windowStatus(challenge: Challenge, nowMs: number): WindowStatus {
  const startMs = challenge.startAt.toMillis?.() ?? challenge.startAt.seconds * 1000;
  const endMs = challenge.endAt.toMillis?.() ?? challenge.endAt.seconds * 1000;
  if (nowMs < startMs) return 'upcoming';
  if (nowMs >= endMs) return 'ended';
  return 'live';
}

// ── challenge row ──────────────────────────────────────────────────────────

function ChallengeRow({
  tenantId,
  challenge,
  templates,
  isEditing,
  onEdit,
}: {
  readonly tenantId: string;
  readonly challenge: Challenge;
  readonly templates: readonly VoucherTemplate[];
  readonly isEditing: boolean;
  readonly onEdit: () => void;
}) {
  const t = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const name = challenge.name[t.locale] ?? challenge.name['en'] ?? challenge.id;
  const nowMs = Date.now();
  const status = windowStatus(challenge, nowMs);
  const live = isChallengeActive(challenge, nowMs);

  const statusPill =
    status === 'live'
      ? {
          bg: 'rgba(13,122,79,0.10)',
          fg: 'var(--color-primary)',
          label: challenge.active
            ? t.t('dashboard.challenges.windowStatus.live')
            : t.t('dashboard.challenges.status.paused'),
        }
      : status === 'upcoming'
        ? {
            bg: 'var(--color-border)',
            fg: 'var(--color-on-surface-muted)',
            label: t.t('dashboard.challenges.windowStatus.upcoming'),
          }
        : {
            bg: 'rgba(239,68,68,0.10)',
            fg: 'var(--color-accent)',
            label: t.t('dashboard.challenges.windowStatus.ended'),
          };

  async function toggleActive() {
    setBusy(true);
    try {
      await challengesRepoFor(tenantId).update(challenge.id, {
        active: !challenge.active,
        updatedAt: Timestamp.now(),
      });
    } catch (e) {
      logger.error('challenge toggle failed', { error: String(e), id: challenge.id });
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await challengesRepoFor(tenantId).delete(challenge.id);
      logger.info('challenge deleted', { tenantId, id: challenge.id });
      // Row disappears when the snapshot updates.
    } catch (e) {
      logger.error('challenge delete failed', { error: String(e), id: challenge.id });
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: isEditing ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
        background: isEditing ? 'rgba(13,122,79,0.04)' : 'var(--color-surface)',
        opacity: live ? 1 : 0.82,
      }}
    >
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontWeight: 500,
            overflowWrap: 'anywhere',
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
          }}
        >
          <span aria-hidden>{challenge.icon}</span>
          {name}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {describeCriterion(challenge.criterion, t)} ·{' '}
          {describeReward(challenge.reward, templates, t)}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.challenges.row.window', {
            start: formatWindowStart(challenge),
            end: formatWindowEnd(challenge),
          })}
        </p>
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            background: statusPill.bg,
            color: statusPill.fg,
            padding: '4px 10px',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {statusPill.label}
        </span>
        {confirmingDelete ? (
          <>
            <button
              type="button"
              onClick={() => void doDelete()}
              disabled={busy}
              style={dangerButtonStyle}
            >
              {busy
                ? t.t('dashboard.challenges.row.deleting')
                : t.t('dashboard.challenges.row.confirmDelete')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              style={miniGhostButtonStyle}
            >
              {t.t('dashboard.challenges.row.cancel')}
            </button>
          </>
        ) : (
          <>
            {status !== 'ended' ? (
              <button
                type="button"
                onClick={() => void toggleActive()}
                disabled={busy}
                style={miniGhostButtonStyle}
              >
                {challenge.active
                  ? t.t('dashboard.challenges.row.pause')
                  : t.t('dashboard.challenges.row.resume')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onEdit}
              disabled={isEditing}
              style={miniGhostButtonStyle}
            >
              {isEditing
                ? t.t('dashboard.challenges.row.editing')
                : t.t('dashboard.challenges.row.edit')}
            </button>
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              {t.t('dashboard.challenges.row.delete')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── challenge form (create + edit) ──────────────────────────────────────────

type GoalKind = 'visits' | 'spend';
type RewardKind = 'none' | 'points' | 'voucher';

interface FormState {
  name: string;
  icon: string;
  description: string;
  goalKind: GoalKind;
  goalValue: string; // visits count OR RM (major units) depending on goalKind
  rewardKind: RewardKind;
  rewardPoints: string;
  rewardVoucherId: string;
  startDate: string; // yyyy-mm-dd
  endDate: string; // yyyy-mm-dd (inclusive last day)
  active: boolean;
}

function ChallengeForm({
  tenantId,
  editing,
  templates,
  onDone,
  onCancelEdit,
}: {
  readonly tenantId: string;
  readonly editing: Challenge | null;
  readonly templates: readonly VoucherTemplate[];
  readonly onDone: () => void;
  readonly onCancelEdit: () => void;
}) {
  const t = useI18n();
  const initial = useMemo(() => formFromChallenge(editing), [editing]);
  const [state, setState] = useState<FormState>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  // Reset every field when the edited challenge changes (toggle Edit / Cancel).
  useEffect(() => {
    setState(initial);
    setError(null);
    setSavedNotice(null);
  }, [editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(p: Partial<FormState>) {
    setState((s) => ({ ...s, ...p }));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSavedNotice(null);

    const trimmedName = state.name.trim();
    if (!trimmedName) {
      setError(t.t('dashboard.challenges.form.errName'));
      return;
    }
    const goalNum = Number(state.goalValue);
    if (!Number.isFinite(goalNum) || goalNum <= 0) {
      setError(t.t('dashboard.challenges.form.errGoal'));
      return;
    }
    const criterion: ChallengeCriterion =
      state.goalKind === 'visits'
        ? { kind: 'visits', n: Math.max(1, Math.round(goalNum)) }
        : { kind: 'spend', amountSen: Math.max(1, Math.round(goalNum * 100)) };

    let reward: ChallengeReward;
    if (state.rewardKind === 'points') {
      const pts = Number(state.rewardPoints);
      if (!Number.isFinite(pts) || pts <= 0) {
        setError(t.t('dashboard.challenges.form.errPoints'));
        return;
      }
      reward = { kind: 'points', amount: Math.round(pts) };
    } else if (state.rewardKind === 'voucher') {
      if (!state.rewardVoucherId) {
        setError(t.t('dashboard.challenges.form.errVoucher'));
        return;
      }
      reward = { kind: 'voucher', voucherTemplateId: state.rewardVoucherId };
    } else {
      reward = { kind: 'none' };
    }

    const startMs = Date.parse(state.startDate);
    const endDayMs = Date.parse(state.endDate);
    if (!Number.isFinite(startMs) || !Number.isFinite(endDayMs)) {
      setError(t.t('dashboard.challenges.form.errDates'));
      return;
    }
    // Store endAt as the midnight AFTER the picked end day so the window (which
    // is half-open, `start <= now < end`) includes the entire last day.
    const endMs = endDayMs + 24 * 60 * 60 * 1000;
    if (!(endMs > startMs)) {
      setError(t.t('dashboard.challenges.form.errDates'));
      return;
    }

    setSubmitting(true);
    try {
      const now = Timestamp.now();
      const startAt = Timestamp.fromMillis(startMs);
      const endAt = Timestamp.fromMillis(endMs);
      const icon = state.icon.trim() || '🎯';
      const descriptionEn = state.description.trim();

      if (editing) {
        const next: Challenge = {
          ...editing,
          name: { ...(editing.name ?? {}), en: trimmedName },
          icon,
          description: descriptionEn
            ? { ...(editing.description ?? {}), en: descriptionEn }
            : editing.description && Object.keys(editing.description).length > 0
              ? editing.description
              : { en: trimmedName },
          criterion,
          reward,
          startAt,
          endAt,
          active: state.active,
          updatedAt: now,
        };
        await challengesRepoFor(tenantId).set(editing.id, next);
        setSavedNotice(t.t('dashboard.challenges.form.updated', { name: trimmedName }));
        onDone();
      } else {
        const id = `chl-${Math.random().toString(36).slice(2, 8)}`;
        const next: Challenge = {
          id,
          name: { en: trimmedName },
          icon,
          description: { en: descriptionEn || trimmedName },
          criterion,
          reward,
          startAt,
          endAt,
          active: state.active,
          createdAt: now,
          updatedAt: now,
        };
        await challengesRepoFor(tenantId).set(id, next);
        setSavedNotice(t.t('dashboard.challenges.form.created', { name: trimmedName }));
        setState(formFromChallenge(null));
      }
    } catch (e2) {
      logger.error('challenge save failed', { error: String(e2), editing: editing?.id });
      setError((e2 as Error).message ?? t.t('dashboard.challenges.form.errSave'));
    } finally {
      setSubmitting(false);
    }
  }

  const isEditMode = editing !== null;

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {isEditMode
            ? t.t('dashboard.challenges.form.editTitle', { name: editing.name['en'] ?? editing.id })
            : t.t('dashboard.challenges.form.createTitle')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {isEditMode
            ? t.t('dashboard.challenges.form.editSubtitle')
            : t.t('dashboard.challenges.form.createSubtitle')}
        </p>
      </header>

      {savedNotice ? (
        <p
          role="status"
          style={{
            margin: 0,
            color: 'var(--color-primary)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
          }}
        >
          {savedNotice}
        </p>
      ) : null}

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.challenges.form.nameLabel')}</span>
        <input
          required
          value={state.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder={t.t('dashboard.challenges.form.namePlaceholder')}
          style={inputStyle}
        />
      </label>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 88px) minmax(0, 1fr)',
          gap: 'var(--space-3)',
        }}
      >
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.challenges.form.iconLabel')}</span>
          <input
            value={state.icon}
            onChange={(e) => patch({ icon: e.target.value })}
            placeholder="🎯"
            style={{ ...inputStyle, textAlign: 'center' }}
          />
        </label>
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.challenges.form.descLabel')}</span>
          <input
            value={state.description}
            onChange={(e) => patch({ description: e.target.value })}
            placeholder={t.t('dashboard.challenges.form.descPlaceholder')}
            style={inputStyle}
          />
        </label>
      </div>

      {/* Goal */}
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.challenges.form.goalLegend')}</legend>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            aria-pressed={state.goalKind === 'visits'}
            onClick={() => patch({ goalKind: 'visits' })}
            style={chipStyle(state.goalKind === 'visits')}
          >
            {t.t('dashboard.challenges.form.goalVisits')}
          </button>
          <button
            type="button"
            aria-pressed={state.goalKind === 'spend'}
            onClick={() => patch({ goalKind: 'spend' })}
            style={chipStyle(state.goalKind === 'spend')}
          >
            {t.t('dashboard.challenges.form.goalSpend')}
          </button>
        </div>
        <label style={{ ...fieldGrid, marginTop: 'var(--space-3)' }}>
          <span style={fieldLabel}>
            {state.goalKind === 'visits'
              ? t.t('dashboard.challenges.form.visitsLabel')
              : t.t('dashboard.challenges.form.spendLabel')}
          </span>
          <input
            type="number"
            min={1}
            step={state.goalKind === 'visits' ? 1 : 'any'}
            value={state.goalValue}
            onChange={(e) => patch({ goalValue: e.target.value })}
            style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
      </fieldset>

      {/* Reward */}
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={fieldLabel}>{t.t('dashboard.challenges.form.rewardLegend')}</legend>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            aria-pressed={state.rewardKind === 'none'}
            onClick={() => patch({ rewardKind: 'none' })}
            style={chipStyle(state.rewardKind === 'none')}
          >
            {t.t('dashboard.challenges.form.rewardNone')}
          </button>
          <button
            type="button"
            aria-pressed={state.rewardKind === 'points'}
            onClick={() => patch({ rewardKind: 'points' })}
            style={chipStyle(state.rewardKind === 'points')}
          >
            {t.t('dashboard.challenges.form.rewardPoints')}
          </button>
          <button
            type="button"
            aria-pressed={state.rewardKind === 'voucher'}
            onClick={() => patch({ rewardKind: 'voucher' })}
            style={chipStyle(state.rewardKind === 'voucher')}
          >
            {t.t('dashboard.challenges.form.rewardVoucher')}
          </button>
        </div>

        {state.rewardKind === 'points' ? (
          <label style={{ ...fieldGrid, marginTop: 'var(--space-3)' }}>
            <span style={fieldLabel}>{t.t('dashboard.challenges.form.pointsLabel')}</span>
            <input
              type="number"
              min={1}
              step={1}
              value={state.rewardPoints}
              onChange={(e) => patch({ rewardPoints: e.target.value })}
              style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums' }}
            />
          </label>
        ) : null}

        {state.rewardKind === 'voucher' ? (
          templates.length === 0 ? (
            <div style={{ ...emptyStyle, marginTop: 'var(--space-3)' }}>
              <p style={{ ...helpTextStyle, margin: 0 }}>
                {t.t('dashboard.challenges.form.noTemplates')}
              </p>
              <Link
                to="/loyalty/vouchers"
                style={{
                  ...primaryButtonStyle,
                  marginTop: 'var(--space-3)',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                {t.t('dashboard.challenges.form.createVoucher')}
              </Link>
            </div>
          ) : (
            <label style={{ ...fieldGrid, marginTop: 'var(--space-3)' }}>
              <span style={fieldLabel}>{t.t('dashboard.challenges.form.rewardVoucherLabel')}</span>
              <select
                value={state.rewardVoucherId}
                onChange={(e) => patch({ rewardVoucherId: e.target.value })}
                style={inputStyle}
              >
                <option value="">{t.t('dashboard.challenges.form.rewardVoucherNone')}</option>
                {templates.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name[t.locale] ?? tpl.name['en'] ?? tpl.id}
                  </option>
                ))}
              </select>
            </label>
          )
        ) : null}
      </fieldset>

      {/* Window */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 'var(--space-3)',
        }}
      >
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.challenges.form.startLabel')}</span>
          <input
            type="date"
            value={state.startDate}
            onChange={(e) => patch({ startDate: e.target.value })}
            style={inputStyle}
          />
        </label>
        <label style={fieldGrid}>
          <span style={fieldLabel}>{t.t('dashboard.challenges.form.endLabel')}</span>
          <input
            type="date"
            value={state.endDate}
            min={state.startDate || undefined}
            onChange={(e) => patch({ endDate: e.target.value })}
            style={inputStyle}
          />
        </label>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="checkbox"
          checked={state.active}
          onChange={(e) => patch({ active: e.target.checked })}
          style={{ width: 18, height: 18, cursor: 'pointer' }}
        />
        <span style={fieldLabel}>
          {state.active
            ? t.t('dashboard.challenges.form.activeOn')
            : t.t('dashboard.challenges.form.activeOff')}
        </span>
      </label>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...primaryButtonStyle,
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting
            ? t.t('dashboard.challenges.form.saving')
            : isEditMode
              ? t.t('dashboard.challenges.form.saveChanges')
              : t.t('dashboard.challenges.form.create')}
        </button>
        {isEditMode ? (
          <button
            type="button"
            onClick={onCancelEdit}
            disabled={submitting}
            style={ghostButtonStyle}
          >
            {t.t('dashboard.challenges.form.cancelEdit')}
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function tsMs(ts: Challenge['startAt']): number {
  return ts.toMillis?.() ?? ts.seconds * 1000;
}

/** yyyy-mm-dd for the window start (UTC-midnight anchored). */
function startDateInput(ts: Challenge['startAt']): string {
  return new Date(tsMs(ts)).toISOString().slice(0, 10);
}

/** yyyy-mm-dd for the INCLUSIVE last day (endAt is stored as next-midnight). */
function endDateInput(ts: Challenge['endAt']): string {
  return new Date(tsMs(ts) - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function formatWindowStart(challenge: Challenge): string {
  return new Date(tsMs(challenge.startAt)).toLocaleDateString();
}

/** Display the inclusive last day (endAt − 1ms lands inside it). */
function formatWindowEnd(challenge: Challenge): string {
  return new Date(tsMs(challenge.endAt) - 1).toLocaleDateString();
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysInput(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function formFromChallenge(c: Challenge | null): FormState {
  if (!c) {
    return {
      name: '',
      icon: '🎯',
      description: '',
      goalKind: 'visits',
      goalValue: '5',
      rewardKind: 'none',
      rewardPoints: '100',
      rewardVoucherId: '',
      startDate: todayInput(),
      endDate: plusDaysInput(30),
      active: true,
    };
  }
  const goalKind: GoalKind = c.criterion.kind === 'spend' ? 'spend' : 'visits';
  const goalValue =
    c.criterion.kind === 'spend' ? String(c.criterion.amountSen / 100) : String(c.criterion.n);
  const rewardKind: RewardKind = c.reward.kind;
  return {
    name: c.name['en'] ?? c.id,
    icon: c.icon || '🎯',
    description: c.description['en'] ?? '',
    goalKind,
    goalValue,
    rewardKind,
    rewardPoints: c.reward.kind === 'points' ? String(c.reward.amount) : '100',
    rewardVoucherId: c.reward.kind === 'voucher' ? c.reward.voucherTemplateId : '',
    startDate: startDateInput(c.startAt),
    endDate: endDateInput(c.endAt),
    active: c.active,
  };
}

function describeCriterion(criterion: ChallengeCriterion, t: I18n): string {
  return criterion.kind === 'visits'
    ? t.t('dashboard.challenges.row.criterionVisits', { n: criterion.n })
    : t.t('dashboard.challenges.row.criterionSpend', { amount: money.format(criterion.amountSen) });
}

function describeReward(
  reward: ChallengeReward,
  templates: readonly VoucherTemplate[],
  t: I18n,
): string {
  switch (reward.kind) {
    case 'points':
      return t.t('dashboard.challenges.row.rewardPoints', { n: reward.amount });
    case 'voucher': {
      const tpl = templates.find((x) => x.id === reward.voucherTemplateId);
      const name = tpl
        ? (tpl.name[t.locale] ?? tpl.name['en'] ?? tpl.id)
        : reward.voucherTemplateId;
      return t.t('dashboard.challenges.row.rewardVoucher', { name });
    }
    case 'none':
      return t.t('dashboard.challenges.row.rewardNone');
  }
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

// ── styles (mirror /loyalty/vouchers) ──────────────────────────────────────

const disabledBannerStyle: React.CSSProperties = {
  marginBottom: 'var(--space-5)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid rgba(234,179,8,0.28)',
  background: 'rgba(234,179,8,0.08)',
};

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)', minWidth: 0 };

const helpTextStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const emptyStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px dashed var(--color-border-strong)',
  textAlign: 'center',
};

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};
